import { ThemeProvider } from "@mui/material/styles";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import "./componentLibrary/styles/QuillCustomStyles.css";
import Footer from "./components/Footer";
import FooterNotification from "./components/FooterNotification";
import Header from "./components/Header";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AuthProvider } from "./contexts/AuthContext";
import {
  PermissionProvider,
  usePermissions,
} from "./contexts/PermissionContext";
import { VideoPreferencesProvider } from "./contexts/VideoPreferencesContext";
import { useTutorialCounter } from "./hooks/useTutorialCounter";
import AnalyticsDashboard from "./pages/AnalyticsDashboard/AnalyticsDashboard";
import BusinessLandingPage from "./pages/BusinessLandingPage";
import ComponentLibraryPage from "./pages/ComponentLibraryPage/ComponentLibraryPage";
import ContentManagementPage from "./pages/ContentManagementPage/ContentManagementPage";
import CreateLessonPage from "./pages/CreateLessonPage/CreateLessonPage";
import DraftTopicsPage from "./pages/DraftTopics/DraftTopicsPage";
import EditLessonPage from "./pages/EditLessonPage/EditLessonPage";
import ErrorPage from "./pages/ErrorPage";
import ExplorePage from "./pages/ExplorePage/ExplorePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForgotUsernamePage from "./pages/ForgotUsernamePage";
import ImageManagementPage from "./pages/ImageManagementPage/ImageManagementPage";
import LandingPage from "./pages/LandingPage";
import LessonPage from "./pages/LessonPage/LessonPage";
import LoginPage from "./pages/LoginPage";
import TopicPage from "./pages/TopicPage";
import SetNewPasswordPage from "./pages/SetNewPasswordPage";
import SignupPage from "./pages/SignupPage";
import TutorialPage from "./pages/TutorialPage";
import VerificationCodePage from "./pages/VerificationCodePage";
import VerificationSentPage from "./pages/VerificationSentPage";
import theme from "./theme";
import { useAuth } from "./contexts/AuthContext";

import { AnnouncementManager } from "./components/AnnouncementManager";
import ChangeDisplayNameModal from "./components/ChangeDisplayNameModal";
import LegalAcceptanceModal from "./components/LegalAcceptanceModal";
import { FeatureFlagRoute } from "./components/FeatureFlagRoute";
import { FeatureFlag } from "./constants/featureFlags";
import { ProtectedRoute } from "./components/RBACComponents";
import { useAnnouncementState } from "./hooks/useAnnouncementState";
import { useLegalComplianceGuard } from "./hooks/useLegalComplianceGuard";
import AboutUsPage from "./pages/AboutUsPage/AboutUsPage";
import AccessCodePage from "./pages/AccessCodePage/AccessCodePage";
import { Admin } from "./pages/Admin";
import AnnouncementManagerPage from "./pages/AnnouncementManagerPage/AnnouncementManagerPage";
import ConfirmIdentity from "./pages/ConfirmIdentity";
import ConfirmUserPage from "./pages/ConfirmUserPage";
import EULAPage from "./pages/EULAPage";
import LegalPage from "./pages/LegalPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import TeleprompterPage from "./pages/TeleprompterPage/TeleprompterPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import MyToolboxPage from "./pages/Toolbox/Toolbox";
import ToolboxLayout from "./pages/ToolboxPage/ToolboxLayout";
import { ToolboxPage } from "./pages/ToolboxPage/ToolboxPage";
import UserRoleManagementPage from "./pages/UserRoleManagementPage/UserRoleManagementPage";
import RolePermissionManagementPage from "./pages/RolePermissionManagementPage/RolePermissionManagementPage";
import ContentDeletePage from "./pages/ContentDeletePage/ContentDeletePage";
import { PermissionEnum } from "./types/rbac";

declare global {
  interface Window {
    hideLoadingUI?: () => void;
  }
}

const App = () => {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <FeatureFlagProvider>
            <NotificationProvider>
              <VideoPreferencesProvider>
                <PermissionProvider>
                  <AppContent />
                </PermissionProvider>
              </VideoPreferencesProvider>
            </NotificationProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

const AuthRedirect = () => {
  const navigate = useNavigate();
  const { isAuthenticated: isAuth, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuth) {
      navigate("/explore");
    }
  }, [isAuth, loading, navigate]);

  if (loading) return null;

  if (!isAuth) {
    return <LandingPage />;
  }

  return null;
};

/**
 * LegalComplianceWrapper
 * Wraps authenticated routes to show legal acceptance modal when needed
 * This is separate from RBAC permission checking
 */
const LegalComplianceWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const [showAnnouncementsAfterLegal, setShowAnnouncementsAfterLegal] =
    useState(false);
  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);

  const { isAuthenticated: isAuth } = useAuth();
  const { userProfile, loading: profileLoading } = usePermissions();

  const {
    showModal: showLegalModal,
    complianceStatus,
    isLoading: legalLoading,
    isAccepting,
    handleAccept,
    handleDecline,
  } = useLegalComplianceGuard(isAuth);

  const { showAnnouncements, clearAnnouncements, triggerAnnouncements } =
    useAnnouncementState();

  useEffect(() => {
    const shouldShowAnnouncements =
      sessionStorage.getItem("showLoginAnnouncements") === "true" ||
      location.state?.showAnnouncements;

    if (shouldShowAnnouncements && !showLegalModal && !legalLoading) {
      setShowAnnouncementsAfterLegal(true);
    }
  }, [showLegalModal, legalLoading, location.state]);

  useEffect(() => {
    if (showAnnouncementsAfterLegal && !showLegalModal) {
      triggerAnnouncements();
      setShowAnnouncementsAfterLegal(false);
    }
  }, [showAnnouncementsAfterLegal, showLegalModal, triggerAnnouncements]);

  // Show display name prompt once legal and announcements are settled and user has no displayName
  useEffect(() => {
    if (
      isAuth &&
      !profileLoading &&
      !legalLoading &&
      !showLegalModal &&
      !showAnnouncements &&
      userProfile &&
      !userProfile.displayName
    ) {
      setShowDisplayNameModal(true);
    }
  }, [
    isAuth,
    profileLoading,
    legalLoading,
    showLegalModal,
    showAnnouncements,
    userProfile,
  ]);

  const handleLegalAccept = async (
    emailAccountIssues: boolean,
    emailFeedbackRequests: boolean,
    emailNewsUpdates: boolean,
  ) => {
    await handleAccept(
      emailAccountIssues,
      emailFeedbackRequests,
      emailNewsUpdates,
    );
    const shouldShowAnnouncements =
      sessionStorage.getItem("showLoginAnnouncements") === "true" ||
      location.state?.showAnnouncements;

    if (shouldShowAnnouncements) {
      setTimeout(() => {
        triggerAnnouncements();
      }, 500);
    }
  };

  if (legalLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking compliance...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      {complianceStatus && (
        <LegalAcceptanceModal
          open={showLegalModal}
          complianceStatus={complianceStatus}
          onAccept={handleLegalAccept}
          onDecline={handleDecline}
          loading={isAccepting}
        />
      )}
      <AnnouncementManager
        showAnnouncements={showAnnouncements && !showLegalModal}
        onComplete={clearAnnouncements}
      />
      <ChangeDisplayNameModal
        isOpen={showDisplayNameModal}
        onClose={() => setShowDisplayNameModal(false)}
        title="What would you like to be called?"
        isInitialPrompt={true}
      />
    </>
  );
};

const AppContent = () => {
  useTutorialCounter();
  const location = useLocation();

  // Hide loading UI when app is ready
  useEffect(() => {
    if (window.hideLoadingUI && typeof window.hideLoadingUI === "function") {
      window.hideLoadingUI();
    }
  }, []);

  // Scroll to top on route change, but handle hash navigation
  useEffect(() => {
    // If there's a hash in the URL, let the browser handle scrolling to it
    if (location.hash) {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      // Only scroll to top if there's no hash
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  const noHeaderRoutes = [
    "/confirm",
    "/set-new-password",
    "/email-verified",
    "/landing",
  ];
  const transparentHeaderRoutes = ["/", "/forgot-password", "/forgot-username"];

  return (
    <div className="min-h-screen flex flex-col w-full overflow-x-hidden">
      <FooterNotification />
      {!noHeaderRoutes.includes(location.pathname) && (
        <Header
          transparent={transparentHeaderRoutes.includes(location.pathname)}
        />
      )}
      <div className="flex flex-col flex-grow pt-12 pb-6 px-0">
        <Routes>
          <Route path="/" element={<AuthRedirect />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/business" element={<BusinessLandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verification-sent" element={<VerificationSentPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/forgot-username" element={<ForgotUsernamePage />} />
          <Route path="/verification-code" element={<VerificationCodePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/confirmUser" element={<ConfirmUserPage />} />
          <Route
            path="/confirm-identity"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <ConfirmIdentity />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/set-new-password" element={<SetNewPasswordPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/end-user-license-agreement" element={<EULAPage />} />
          <Route
            path="/tutorial"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <TutorialPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/component-library"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <ComponentLibraryPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/explore"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <ExplorePage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/topics/:moduleId"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <TopicPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/topics/:moduleId/sections/:lessonId"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <LessonPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <ProfilePage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute
                permission={[
                  PermissionEnum.OWNER,
                  PermissionEnum.CONTENT_ALL,
                  PermissionEnum.CONTENT_READ,
                  PermissionEnum.CONTENT_DELETE,
                  PermissionEnum.ANALYTICS_READ,
                  PermissionEnum.ACCESS_CODE_READ,
                  PermissionEnum.ANNOUNCEMENT_ALL,
                ]}
              >
                <LegalComplianceWrapper>
                  <Admin.Layout />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          >
            <Route index element={<Admin.Index />} />
            <Route
              path="content-management"
              element={
                <ProtectedRoute
                  permission={[
                    PermissionEnum.CONTENT_CREATE,
                    PermissionEnum.CONTENT_READ,
                    PermissionEnum.CONTENT_UPDATE,
                  ]}
                >
                  <ContentManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="content-delete"
              element={
                <FeatureFlagRoute flag={FeatureFlag.CONTENT_DELETION}>
                  <ProtectedRoute
                    permission={[
                      PermissionEnum.CONTENT_DELETE,
                      PermissionEnum.CONTENT_READ,
                    ]}
                    requireAll
                  >
                    <ContentDeletePage />
                  </ProtectedRoute>
                </FeatureFlagRoute>
              }
            />
            <Route
              path="draft-topics"
              element={
                <ProtectedRoute permission={PermissionEnum.CONTENT_READ}>
                  <DraftTopicsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="create-lesson"
              element={
                <ProtectedRoute permission={PermissionEnum.CONTENT_CREATE}>
                  <CreateLessonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="edit-lesson/:lessonId"
              element={
                <ProtectedRoute permission={PermissionEnum.CONTENT_UPDATE}>
                  <EditLessonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics-dashboard"
              element={
                <ProtectedRoute permission={PermissionEnum.ANALYTICS_READ}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="image-management"
              element={
                <ProtectedRoute
                  permission={[
                    PermissionEnum.CONTENT_ALL,
                    PermissionEnum.CONTENT_READ,
                    PermissionEnum.CONTENT_CREATE,
                    PermissionEnum.CONTENT_UPDATE,
                  ]}
                >
                  <ImageManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="user-role-management"
              element={
                <ProtectedRoute permission={PermissionEnum.OWNER}>
                  <UserRoleManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="role-permission-management"
              element={
                <ProtectedRoute permission={PermissionEnum.OWNER}>
                  <RolePermissionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="access-codes"
              element={
                <ProtectedRoute permission={PermissionEnum.ACCESS_CODE_READ}>
                  <AccessCodePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="announcement-manager"
              element={
                <ProtectedRoute permission={PermissionEnum.ANNOUNCEMENT_ALL}>
                  <AnnouncementManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="toolboxes"
              element={
                <FeatureFlagRoute
                  flag={FeatureFlag.NEW_TOOLBOXES}
                  fallbackPath="/error"
                >
                  <Admin.Toolboxes.Layout />
                </FeatureFlagRoute>
              }
            >
              <Route index element={<Admin.Toolboxes.Index />} />
              <Route path=":topicId" element={<Admin.Toolboxes.Show />} />
              <Route
                path=":topicId/tools"
                element={<Admin.Toolboxes.Tools />}
              />
              <Route
                path=":topicId/profile-definitions"
                element={<Admin.Toolboxes.ProfileDefinitions />}
              />
              <Route
                path=":topicId/profile-definitions/new"
                element={<Admin.Toolboxes.ProfileDefinitionsNew />}
              />
              <Route
                path=":topicId/profile-definitions/:definitionId/edit"
                element={<Admin.Toolboxes.ProfileDefinitionsEdit />}
              />
              <Route
                path=":topicId/checkin-definitions"
                element={<Admin.Toolboxes.CheckinDefinitions />}
              />
              <Route
                path=":topicId/checkin-definitions/new"
                element={<Admin.Toolboxes.CheckinDefinitionsNew />}
              />
              <Route
                path=":topicId/checkin-definitions/:definitionId/edit"
                element={<Admin.Toolboxes.CheckinDefinitionsEdit />}
              />
            </Route>
          </Route>
          <Route
            path="/my-toolboxes/:topicId"
            element={
              <ProtectedRoute>
                <LegalComplianceWrapper>
                  <MyToolboxPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/toolboxes/:topicId"
            element={
              <FeatureFlagRoute
                flag={FeatureFlag.NEW_TOOLBOXES}
                fallbackPath="/my-toolboxes/:topicId"
              >
                <ProtectedRoute>
                  <LegalComplianceWrapper>
                    <ToolboxLayout />
                  </LegalComplianceWrapper>
                </ProtectedRoute>
              </FeatureFlagRoute>
            }
          >
            <Route index element={<ToolboxPage.Overview />} />
            <Route path="responses" element={<ToolboxPage.Responses />} />
            <Route path="profile" element={<ToolboxPage.Profile />} />
            <Route path="plans/new" element={<ToolboxPage.Plans.Create />} />
            <Route path="plans/:planId" element={<ToolboxPage.Plans.Show />} />
            <Route
              path="plans/:planId/edit"
              element={<ToolboxPage.Plans.Edit />}
            />
            <Route path="check-ins" element={<ToolboxPage.CheckIns />} />
            <Route path="check-ins/new" element={<ToolboxPage.CheckInsNew />} />
            <Route
              path="check-ins/:id/edit"
              element={<ToolboxPage.CheckInsEdit />}
            />
          </Route>
          <Route
            path="/teleprompters/:lessonId"
            element={
              <ProtectedRoute permission={PermissionEnum.CONTENT_READ}>
                <LegalComplianceWrapper>
                  <TeleprompterPage />
                </LegalComplianceWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
};

export default App;
